import { useCallback, useSyncExternalStore } from "react";

/**
 * The md boundary, as a media query.
 *
 * It is written once here because two things have to agree about it: these
 * hooks, and the `md:` utilities in the markup. A layout that switches at one
 * width while its styles switch at another is worse than either width.
 */
export const MOBILE_QUERY = "(max-width: 767px)";

/** A pointer that cannot hover, which is what actually distinguishes touch. */
export const COARSE_POINTER_QUERY = "(pointer: coarse)";

/**
 * Subscribes to a media query.
 *
 * `useSyncExternalStore` rather than state in an effect: the value is read
 * during the first render, so a phone paints the mobile layout once instead of
 * painting the desktop one and swapping. That matters here because the two
 * layouts are different markup, not the same markup restyled.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    // Nothing is rendered on a server, but the store contract wants an answer
    // for an environment with no window: the desktop layout is the safer of
    // the two to render blind, since it degrades to a scroller rather than to
    // a missing control.
    () => false,
  );
}

/**
 * Whether the viewport is narrower than the md breakpoint.
 *
 * Deliberately a width question and not a device question: a narrow desktop
 * window has the same problem a phone does, and the answer -- cards instead of
 * a wide table -- is the same.
 */
export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_QUERY);
}

/**
 * Whether the primary pointer is coarse, i.e. a finger.
 *
 * Used for the things that depend on the input device rather than on the
 * width: not opening the soft keyboard the instant a dialog mounts, for one.
 */
export function useIsTouch(): boolean {
  return useMediaQuery(COARSE_POINTER_QUERY);
}
